// @ts-nocheck
/* ============================================================
   Fence Works — automatic slope / grade detection
   Samples each fence run, pulls public elevation data (USGS EPQS,
   Open-Elevation fallback), and computes a slope % + grade tier.
   Elevations are cached by rounded lat/lng so edits don't re-query.
   Exposed as window.VFP_SLOPE
   ============================================================ */
import { tierFor } from "./grade";   // shared with measured grade profiles
(function () {
  "use strict";
  const M_TO_FT = 3.28084;
  const cache = new Map();         // "lat,lng" (5dp) -> elevation ft
  const key = (lat, lng) => lat.toFixed(5) + "," + lng.toFixed(5);

  function fetchT(url, opts, ms) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms || 6000);
    return fetch(url, Object.assign({ signal: ctrl.signal }, opts || {})).finally(() => clearTimeout(t));
  }

  // global concurrency gate so many runs detecting at once don't flood USGS
  let active = 0; const waiters = [];
  function acquire() { return active < 6 ? (active++, Promise.resolve()) : new Promise(res => waiters.push(res)).then(() => { active++; }); }
  function release() { active--; const w = waiters.shift(); if (w) w(); }
  async function gated(fn) { await acquire(); try { return await fn(); } finally { release(); } }

  function feetBetween(a, b) {
    return window.L ? window.L.latLng(a[0], a[1]).distanceTo(window.L.latLng(b[0], b[1])) * M_TO_FT
                    : Math.hypot((a[0] - b[0]) * 364000, (a[1] - b[1]) * 364000 * Math.cos(a[0] * Math.PI / 180));
  }

  // ---- sample a run (array of [lat,lng] vertices) every ~40 ft, cap ~12 ----
  function sampleRun(verts, stepFt) {
    stepFt = stepFt || 40;
    const pts = [];
    for (let i = 1; i < verts.length; i++) {
      const a = verts[i - 1], b = verts[i];
      const d = feetBetween(a, b);
      const n = Math.max(1, Math.round(d / stepFt));
      for (let k = (i === 1 ? 0 : 1); k <= n; k++) {
        const t = k / n;
        pts.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
      }
    }
    // cap at ~12 by decimation, but always keep endpoints
    if (pts.length > 12) {
      const out = [pts[0]];
      const stride = (pts.length - 1) / 11;
      for (let i = 1; i < 11; i++) out.push(pts[Math.round(i * stride)]);
      out.push(pts[pts.length - 1]);
      return out;
    }
    return pts;
  }

  // ---- USGS EPQS (one point) ----
  async function usgs(lat, lng) {
    const url = "https://epqs.nationalmap.gov/v1/json?x=" + lng + "&y=" + lat + "&units=Feet&wkid=4326&includeDate=false";
    const r = await fetchT(url, null, 8000);
    if (!r.ok) throw new Error("epqs " + r.status);
    const j = await r.json();
    const v = parseFloat(j && (j.value != null ? j.value : (j.USGS_Elevation_Point_Query_Service && j.USGS_Elevation_Point_Query_Service.Elevation_Query && j.USGS_Elevation_Point_Query_Service.Elevation_Query.Elevation)));
    if (!isFinite(v) || v <= -1000000) throw new Error("epqs no data");
    return v;
  }
  // ---- Open-Elevation (batch, meters) ----
  async function openElev(points) {
    const r = await fetchT("https://api.open-elevation.com/api/v1/lookup", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ locations: points.map(p => ({ latitude: p[0], longitude: p[1] })) }),
    }, 9000);
    if (!r.ok) throw new Error("open-elev " + r.status);
    const j = await r.json();
    return (j.results || []).map(x => x.elevation * M_TO_FT);
  }

  // ---- get elevations for a list of points (cache + parallel) ----
  async function elevations(points) {
    const out = new Array(points.length);
    const need = [], needIdx = [];
    points.forEach((p, i) => { const k = key(p[0], p[1]); if (cache.has(k)) out[i] = cache.get(k); else { need.push(p); needIdx.push(i); } });
    if (!need.length) return out;

    // try USGS for all needed points (globally gated to avoid throttling); collect failures
    const results = await Promise.allSettled(need.map(p => gated(() => usgs(p[0], p[1]))));
    const missIdx = [];
    results.forEach((res, k) => {
      if (res.status === "fulfilled" && isFinite(res.value)) { out[needIdx[k]] = res.value; cache.set(key(need[k][0], need[k][1]), res.value); }
      else missIdx.push(k);
    });
    // fall back to Open-Elevation (batch) for any USGS misses
    if (missIdx.length) {
      const pts = missIdx.map(k => need[k]);
      const vals = await openElev(pts);           // throws -> caller handles
      vals.forEach((v, k) => { if (isFinite(v)) { out[needIdx[missIdx[k]]] = v; cache.set(key(pts[k][0], pts[k][1]), v); } });
    }
    return out;
  }

  // ---- detect slope for one run; verts = [[lat,lng], ...] ----
  async function detectRun(verts) {
    const distFt = (() => { let t = 0; for (let i = 1; i < verts.length; i++) t += feetBetween(verts[i - 1], verts[i]); return t; })();
    if (distFt < 1) return { status: "skip" };
    const pts = sampleRun(verts);
    let elevs;
    try { elevs = await elevations(pts); } catch (e) { return { status: "failed", error: String(e && e.message || e) }; }
    const clean = elevs.filter(v => isFinite(v));
    if (clean.length < 2) return { status: "failed", error: "no elevation data" };
    const mn = Math.min(...clean), mx = Math.max(...clean);
    const riseMax = mx - mn;
    const riseEnds = Math.abs((elevs[elevs.length - 1] || 0) - (elevs[0] || 0));
    // steepest local segment
    let steepest = 0;
    for (let i = 1; i < pts.length; i++) {
      if (!isFinite(elevs[i]) || !isFinite(elevs[i - 1])) continue;
      const d = feetBetween(pts[i - 1], pts[i]); if (d < 1) continue;
      steepest = Math.max(steepest, Math.abs(elevs[i] - elevs[i - 1]) / d * 100);
    }
    const pct = (riseMax / distFt) * 100;
    const t = tierFor(pct);
    return { status: "done", pct, riseMax, riseEnds, distFt, steepest, samples: clean.length, ...t };
  }

  window.VFP_SLOPE = { detectRun, sampleRun, elevations, tierFor, _cache: cache };
})();
